import { useMemo, type JSX } from 'react';

import { makeAccessorFunction } from './makeAccessorFunction.js';
import {
  type CellContext,
  type ColumnDef,
  type ColumnDefTemplate,
} from './useTable/types.js';
import { LogContentHighlight } from '../../utils/log-content-highlighting.js';
import { replaceUrlsWithLinks } from '../../utils/replace-urls-with-links.js';
import {
  applyColumnFormatters,
  formatCellValue,
} from '../features/CellFormatter.js';
import {
  applyColumnTypes,
  getAlignmentBasedOnType,
  isDatatableV2LogContentColumnConfig,
} from '../features/ColumnTypes/ColumnTypes.js';
import { SPACER_COLUMN } from '../features/Layout.js';
import { applySortTypes } from '../features/Sorting/sort-types.js';
import {
  type DataTableV2ColumnDef,
  type DataTableV2DisplayColumnDef,
  type DataTableV2GroupColumnDef,
  type DataTableV2RowData,
} from '../public.api.js';

/**
 * @internal
 * TypeGuard helper to verify that a given external columnDef is a GroupColumnDef
 */
export function isDataTableV2GroupColumnDef<
  TData extends DataTableV2RowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is DataTableV2GroupColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'columns' in columnDef &&
    Array.isArray((columnDef as { columns: Array<unknown> }).columns)
  );
}

/**
 * @internal
 * TypeGuard helper to verify that a given external columnDef is a DisplayColumnDef
 */
export function isDataTableV2DisplayColumnDef<
  TData extends DataTableV2RowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is DataTableV2DisplayColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'accessor' in columnDef
  );
}

/**
 * @internal
 * Helper function to transform the public accessor key to the correct
 * member config for the tanstack/react-table ColumnDef.
 */
function splitAccessor<TData extends DataTableV2RowData, TValue = unknown>(
  column: DataTableV2DisplayColumnDef<TData, TValue>,
): {
  accessorKey?: string;
  accessorFn?: (row: TData) => TValue;
} {
  if (typeof column.accessor === 'string') {
    if (column.accessor.includes('.') || column.accessor.includes('[')) {
      return {
        accessorFn: makeAccessorFunction(column.accessor),
      };
    }

    return {
      accessorKey: column.accessor,
    };
  }

  if (typeof column.accessor === 'function') {
    return {
      accessorFn: column.accessor,
    };
  }
  return {};
}

/**
 * Maps the given header rendere to the internal header renderer and prevents
 * leaking.
 * @internal
 */
function createHeaderRenderer<TData extends DataTableV2RowData>(
  header: DataTableV2ColumnDef<TData>['header'],
) {
  // We need to map the function from the outside here in order to not leak
  // tanstack table internals.
  if (typeof header === 'function') {
    return () => header();
  }
  return header;
}

/**
 * Custom cell renderer function.
 * @public
 */
export type DataTableV2CustomCell<
  TData extends DataTableV2RowData,
  TValue = unknown,
> = (props: {
  /** Raw value of the cell that is rendered. */
  value: TValue;
  /** Index of the row the rendered cell is in. */
  rowIndex: number;
  /** Data of the entire row the rendered cell is in. */
  rowData: TData;
  /** Id of the row the rendered cell is in. */
  rowId: string;
  /** Format function that would have been applied to the cell value out of the box. */
  format: (value: TValue) => string;
  /** Link detection function for automatic link detection.  */
  detectLinks: (value: string) => string | JSX.Element;
  /** Formatting function for log content. */
  formatLogContent: (value: string) => JSX.Element | null;
  /** The current line wrap state of the column the rendered cell is in. */
  isLineWrapped: boolean;
}) => JSX.Element;

/**
 * Prepares all exposed functions for the custom cell renderer.
 */
function createCellRenderer<TData extends DataTableV2RowData, TValue>(
  consumerCustomCell: DataTableV2CustomCell<TData, TValue>,
  consumerColumnDef: DataTableV2DisplayColumnDef<TData, TValue>,
): ColumnDefTemplate<CellContext<TData, TValue>> {
  return ({ getValue, row, column }) => {
    const lineWrap = column.getIsLineWrapped();

    return consumerCustomCell({
      value: getValue(),
      rowIndex: row.index,
      rowData: row.original,
      rowId: row.id,
      format: (value) => formatCellValue(value, consumerColumnDef),
      formatLogContent: (logContent: string) => {
        const truncationLimit = isDatatableV2LogContentColumnConfig(
          consumerColumnDef.config,
        )
          ? consumerColumnDef.config.truncationLimit
          : undefined;
        return (
          <LogContentHighlight
            logContent={logContent}
            lineWrap={lineWrap}
            truncationLimit={truncationLimit}
          />
        );
      },
      detectLinks: replaceUrlsWithLinks,
      isLineWrapped: lineWrap,
    });
  };
}

/**
 * @internal
 * Helper function to convert from the public `DataTableV2DisplayColumnDef` to the internally used
 * ColumnDef.
 */
function createDisplayColumnDef<
  TData extends DataTableV2RowData,
  TValue = unknown,
>(
  columnDef: DataTableV2DisplayColumnDef<TData, TValue>,
): ColumnDef<TData, TValue> {
  const hasFormatter =
    columnDef.columnType !== 'meterbar' &&
    columnDef.columnType !== 'sparkline' &&
    columnDef.columnType !== 'gantt' &&
    !!columnDef.formatter;

  const composedDef: ColumnDef<TData, TValue> = {
    id: columnDef.id,
    header: createHeaderRenderer(columnDef.header),
    width: columnDef.width,
    minWidth: columnDef.minWidth,
    maxWidth: columnDef.maxWidth,
    alignment:
      columnDef.alignment ?? getAlignmentBasedOnType(columnDef.columnType),
    enableSorting: columnDef.disableSorting ? !columnDef.disableSorting : true,
    sortAccessor: columnDef.sortAccessor,
    sortDescFirst: columnDef.sortDescFirst,
    invertSorting: columnDef.sortInverted,
    enableHiding: !columnDef.disableColumnHiding,
    thresholds: columnDef.thresholds,
    meta: {
      config: columnDef.config,
      hasFormatter,
      hasCustomCellRenderer: columnDef.cell !== undefined,
      hasCustomHeaderRenderer: typeof columnDef.header !== 'string',
    },
    ...splitAccessor(columnDef),
    ...applySortTypes(columnDef),
  };

  if (columnDef.columnType) {
    composedDef.columnType = columnDef.columnType;
  }

  if (columnDef.cell) {
    const customCell = columnDef.cell;

    if (typeof customCell === 'string') {
      composedDef.cell = customCell;
      return composedDef;
    }

    if (typeof customCell === 'function') {
      return {
        ...composedDef,
        cell: createCellRenderer(customCell, columnDef),
      };
    }

    return composedDef;
  }

  return {
    ...composedDef,
    ...applyColumnTypes(columnDef, composedDef),
    ...applyColumnFormatters(columnDef),
  };
}

/**
 * Helper hook that will calculate which columns are enabled within the DataTable
 * @internal
 */
export function useColumns<TData extends DataTableV2RowData, TValue = unknown>(
  propColumns: DataTableV2ColumnDef<TData, TValue>[],
) {
  return useMemo(() => {
    const createdColumns: ColumnDef<TData>[] = [];
    const propColumnMap = new Map<
      string,
      DataTableV2ColumnDef<TData, TValue>
    >();

    // Iterate through the columns and create the definitions required
    for (const firstLevelColumn of propColumns) {
      propColumnMap.set(firstLevelColumn.id, firstLevelColumn);
      if (isDataTableV2GroupColumnDef(firstLevelColumn)) {
        const groupColumn: ColumnDef<TData, TValue> = {
          id: firstLevelColumn.id,
          header: createHeaderRenderer(firstLevelColumn.header),
          width: firstLevelColumn.width,
          enableSorting: false,
          columns: firstLevelColumn.columns?.map((childColumn) =>
            createDisplayColumnDef<TData>(childColumn),
          ),
          alignment: firstLevelColumn.alignment,
          meta: {
            hasCustomHeaderRenderer:
              typeof firstLevelColumn.header !== 'string',
          },
        };
        createdColumns.push(groupColumn);

        for (const secondLevelColumn of firstLevelColumn.columns ?? []) {
          propColumnMap.set(
            secondLevelColumn.id,
            secondLevelColumn as DataTableV2DisplayColumnDef<TData, TValue>,
          );
        }
      } else if (isDataTableV2DisplayColumnDef(firstLevelColumn)) {
        createdColumns.push(createDisplayColumnDef<TData>(firstLevelColumn));
      }
    }

    // Insert a last column to spread
    if (createdColumns.length > 0) {
      createdColumns.push(SPACER_COLUMN as ColumnDef<TData>);
    }

    return [createdColumns, propColumnMap] as const;
  }, [propColumns]);
}
